/** Gotenberg briefing snapshot: detect `?print=1`, signal `window.__BRIEFING_READY__`, and timing for chart settle / fallback. */

import { formatDateForUrl, parseDateString } from "@/lib/utils/dateUtils";

declare global {
  interface Window {
    __BRIEFING_READY__?: boolean;
    /** Set by the headless chart image bundle, which has no URL to carry `print=1`. */
    __HERALD_PRINT_SNAPSHOT__?: boolean;
  }
}

export const BRIEFING_PDF_SETTLE_MS = 500;
export const BRIEFING_PDF_FALLBACK_MS = 30_000;

export const isBriefingPdfSnapshot = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }
  if (window.__HERALD_PRINT_SNAPSHOT__ === true) {
    return true;
  }
  return new URLSearchParams(window.location.search).get("print") === "1";
};

export const parseBriefingHasUserPrompt = (value: unknown): boolean =>
  value === true || value === "true" || value === "1";

/** Accepts only a real ISO calendar date supplied by scheduled delivery. */
export const parseBriefingDeliveryDate = (
  value: unknown,
): string | undefined => {
  if (typeof value !== "string") return undefined;
  const date = parseDateString(value);
  return date && formatDateForUrl(date) === value ? value : undefined;
};

export interface BriefingRouteSearch {
  logo?: string;
  hasUserPrompt: boolean;
  deliveryDate?: string;
}

/** Validates untrusted search values for the public briefing route. */
export const parseBriefingRouteSearch = (
  search: Record<string, unknown>,
): BriefingRouteSearch => {
  const logo = typeof search.logo === "string" ? search.logo : undefined;
  return {
    logo: isSafeBriefingLogoUrl(logo) ? logo : undefined,
    hasUserPrompt: parseBriefingHasUserPrompt(search.hasUserPrompt),
    deliveryDate: parseBriefingDeliveryDate(search.deliveryDate),
  };
};

export const signalBriefingPdfReady = (): void => {
  if (typeof window === "undefined") {
    return;
  }
  window.__BRIEFING_READY__ = true;
};

/** Rejects non-https logo URLs from untrusted `?logo=` query params (SSRF / data: URIs). */
export const isSafeBriefingLogoUrl = (
  url: string | undefined,
): url is string => {
  if (!url) {
    return false;
  }
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
};
